import React, { useEffect, useMemo, useState } from "react";
import { Play, Pause, Headphones, Settings, Share2, BarChart2, Radio } from "lucide-react";

const API_BASE = (process.env.REACT_APP_RADIO_URL || "http://203.161.42.145:5100") + "/api/radio";
const STREAM_URL = `${API_BASE.replace(/\/api\/radio$/, "")}/api/radio/play.mp3`;
const PUBLIC_LISTEN_URL = STREAM_URL; // remplace par ton URL publique quand tu auras un domaine

export default function RadioStudio() {
  const [np, setNp] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [audio, setAudio] = useState(null);
  const [serverUp, setServerUp] = useState(true);

  // Simple fetch NowPlaying
  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const r = await fetch(`${API_BASE}/nowplaying`);
        if (!r.ok) throw new Error("np not ok");
        const j = await r.json();
        if (mounted) setNp(j);
      } catch {
        if (mounted) setServerUp(false);
      }
    }
    load();

    const t = setInterval(load, 10_000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  // gérer le player local
  const togglePlay = () => {
    if (!audio) {
      const a = new Audio(STREAM_URL);
      a.crossOrigin = "anonymous";
      a.play().then(() => {
        setAudio(a);
        setPlaying(true);
      }).catch(() => setPlaying(false));
    } else {
      if (playing) {
        audio.pause();
        setPlaying(false);
      } else {
        audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
      }
    }
  };

  const listeners = np?.listeners ?? 0;
  const trackTitle = useMemo(() => {
    const t = np?.title || "";
    const a = np?.artist ? ` • ${np.artist}` : "";
    return t + a || "—";
  }, [np]);

  const shortLink = useMemo(() => {
    // remplace par ton slug si tu veux: ex: https://waveo.net/r/latlas
    return PUBLIC_LISTEN_URL;
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-white">
      {/* Header */}
      <header className="mx-auto max-w-6xl px-4 pt-10 pb-4">
        <h1 className="text-3xl font-bold">🎉 Félicitations ! <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">Votre radio est créée</span></h1>
        <p className="text-gray-600 mt-1">Vous pouvez maintenant lancer votre diffusion et écouter votre flux radio.</p>
      </header>

      <main className="mx-auto max-w-6xl px-4 pb-16 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Colonne principale */}
        <section className="lg:col-span-2 space-y-6">
          {/* Carte Radio */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center text-white">
                <Radio />
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-2xl font-bold">{np?.station || "Ma Radio"}</h2>
                  <span className={`text-xs px-2 py-1 rounded-full ${serverUp ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                    {serverUp ? "En ligne" : "Hors ligne"}
                  </span>
                  <span className="text-xs px-2 py-1 rounded-full bg-purple-50 text-purple-700">Live Streaming</span>
                </div>
                <p className="text-gray-600 mt-1">{trackTitle}</p>
              </div>
            </div>

            {/* Contrôles de diffusion */}
            <div className="mt-6">
              <h3 className="font-semibold text-gray-900 mb-3">Contrôles de diffusion</h3>
              <button
                onClick={togglePlay}
                className={`w-full md:w-auto inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-semibold shadow-sm transition
                ${playing ? "bg-red-600 hover:bg-red-700" : "bg-emerald-600 hover:bg-emerald-700"}`}
              >
                {playing ? <Pause size={18}/> : <Play size={18}/>}
                {playing ? "Arrêter l'écoute locale" : "Lancer ma radio"}
              </button>
            </div>

            {/* Informations techniques */}
            <div className="mt-8">
              <h3 className="font-semibold text-gray-900 mb-3">Informations techniques</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="rounded-xl border border-gray-100 p-4">
                  <p className="text-gray-500">URL de diffusion</p>
                  <a className="text-purple-600 break-all hover:underline" href={STREAM_URL} target="_blank" rel="noreferrer">
                    {STREAM_URL}
                  </a>
                </div>
                <div className="rounded-xl border border-gray-100 p-4">
                  <p className="text-gray-500">Port RSAS</p>
                  <p className="font-medium">5100</p>
                </div>
                <div className="rounded-xl border border-gray-100 p-4">
                  <p className="text-gray-500">Format</p>
                  <p className="font-medium">MP3 – 128 kbps</p>
                </div>
                <div className="rounded-xl border border-gray-100 p-4">
                  <p className="text-gray-500">Auditeurs connectés</p>
                  <p className="font-medium">{listeners}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Colonne droite */}
        <aside className="space-y-6">
          {/* Statistiques en direct */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-900 mb-4">Statistiques en direct</h3>

            <div className="space-y-3">
              <StatRow icon={<Headphones className="w-4 h-4" />} label="Auditeurs" value={listeners} />
              <StatRow icon={<BarChart2 className="w-4 h-4" />} label="Durée de diffusion" value="00:00:00" />
              <StatRow icon={<Radio className="w-4 h-4" />} label="Statut serveur" value={serverUp ? "Actif" : "Inactif"} valueClass={serverUp ? "text-green-700" : "text-gray-600"} />
            </div>

            <div className="border-t mt-4 pt-4 grid grid-cols-2 gap-3">
              <a href="/dashboard" className="inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 bg-gray-50 text-gray-700 hover:bg-gray-100">
                <BarChart2 size={16}/> Tableau de bord
              </a>
              <a href="/radios" className="inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 bg-gray-50 text-gray-700 hover:bg-gray-100">
                <Headphones size={16}/> Écouter d'autres radios
              </a>
              <a href="/settings" className="col-span-2 inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 bg-gray-50 text-gray-700 hover:bg-gray-100">
                <Settings size={16}/> Paramètres
              </a>
            </div>
          </div>

          {/* Partager ma radio */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
            <h3 className="font-semibold text-gray-900 mb-3">Partager ma radio</h3>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={shortLink}
                className="flex-1 rounded-xl border border-gray-200 px-3 py-2 text-sm bg-gray-50"
                onFocus={(e)=>e.currentTarget.select()}
              />
              <button
                onClick={() => navigator.clipboard.writeText(shortLink)}
                className="inline-flex items-center gap-2 rounded-xl px-3 py-2 bg-purple-600 text-white hover:bg-purple-700"
              >
                <Share2 size={16}/> Copier
              </button>
            </div>
          </div>
        </aside>
      </main>
    </div>
  );
}

function StatRow({ icon, label, value, valueClass }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 text-gray-600">
        <span className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center">{icon}</span>
        <span>{label}</span>
      </div>
      <span className={`font-semibold ${valueClass || "text-gray-900"}`}>{value}</span>
    </div>
  );
}

